import { useState } from 'react'
import type { ReactElement } from 'react'
import { useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { CITY } from '@city'
import { StoreLinks } from '../../scripts/storeLinks.mjs'
import { AppDeepLinks } from '../../scripts/appDeepLinks.mjs'
import { TrackingLinks } from '../../scripts/trackingLinks.mjs'
import { installBannerDeepLink } from './installBannerDeepLink'
import { reportStoreOutboundTapped } from '../analytics/storeOutboundEvents'
import { realMetroAnalyticsReporter } from '../analytics/webAnalyticsReporter'
import type { MetroAnalyticsReporter } from '../interaction/metroAnalyticsReporter'
import styles from './AndroidInstallBanner.module.css'

/**
 * Android counterpart of the iOS Smart App Banner (TASK-590).
 *
 * Chrome's native install banners only exist for PWAs — for a native
 * app the web page has to draw its own. This one mirrors what Safari
 * gives iOS for free: app icon, name, one store CTA, dismissible. The
 * CTA is an `intent://` link, so an installed app opens directly on the
 * page's own surface (station page → that station, journey page → the
 * pre-filled planner — same mapping the Smart App Banner's
 * `app-argument` carries), and a visitor without the app falls through
 * to the city's `/go/site-smart-banner` tracking link, which the links
 * service sends on to Play and counts with every other channel
 * (TASK-1170). The tap reports `store_outbound_tapped` with the
 * `web-smart-banner` slot — the same slot iOS uses, per the attribution
 * contract (`wiki/04-channels/web-to-store-attribution-baseline.md`).
 *
 * Dismissal persists in `localStorage`, fail-safe both ways: a storage
 * that throws (private mode, disabled cookies) never breaks the page —
 * the banner simply reappears next visit.
 */

/** `localStorage` key marking the banner dismissed on this browser. */
export const INSTALL_BANNER_DISMISSED_KEY = 'metro.androidInstallBanner.dismissed'

function isDismissed(): boolean {
  try {
    return window.localStorage.getItem(INSTALL_BANNER_DISMISSED_KEY) === '1'
  } catch {
    return false
  }
}

function persistDismissed(): void {
  try {
    window.localStorage.setItem(INSTALL_BANNER_DISMISSED_KEY, '1')
  } catch {
    // Storage unavailable — the dismissal lives for this page only.
  }
}

export interface AndroidInstallBannerProps {
  /**
   * Analytics sink. Optional — defaults to {@link realMetroAnalyticsReporter}.
   * Tests inject a spy.
   */
  readonly reporter?: MetroAnalyticsReporter
}

export function AndroidInstallBanner({
  reporter = realMetroAnalyticsReporter,
}: AndroidInstallBannerProps = {}): ReactElement | null {
  const { t } = useTranslation()
  const location = useLocation()
  const [dismissed, setDismissed] = useState(isDismissed)

  if (!/android/i.test(navigator.userAgent) || dismissed) return null

  const href = AppDeepLinks.androidIntent(
    installBannerDeepLink(location.pathname, location.search),
    TrackingLinks.url(TrackingLinks.siteSource(StoreLinks.SLOTS.SMART_BANNER), CITY.domain),
  )

  return (
    <div className={styles.banner}>
      <button
        type="button"
        className={styles.dismiss}
        aria-label={t('install_banner.dismiss')}
        onClick={() => {
          persistDismissed()
          setDismissed(true)
        }}
      >
        {'×'}
      </button>
      <img src="/apple-touch-icon-v3.png" alt="" className={styles.icon} />
      <div className={styles.copy}>
        <span className={styles.title}>{t('site.name')}</span>
        <span className={styles.subtitle}>{t('install_banner.subtitle')}</span>
      </div>
      {/* Same-tab navigation on purpose: Chrome only resolves intent://
          on a top-level same-tab navigation; target="_blank" breaks the
          app hand-off on several Android browsers. */}
      <a
        className={styles.cta}
        href={href}
        rel="nofollow"
        onClick={() =>
          reportStoreOutboundTapped(reporter, {
            store: 'google_play',
            slot: StoreLinks.SLOTS.SMART_BANNER,
          })
        }
      >
        {t('install_banner.cta')}
      </a>
    </div>
  )
}
